<template>
    <div class="form-switch" :class="{'is-active': isActive, 'min-h-auto': props.hideDetails}">
        <label class="form-switch__content" :class="{'form-switch__column': props.forceColumn, 'form-switch__row': props.forceRow}">
            <span v-if="props.label" class="form-switch__label w-full flex items-center" :class="{'justify-center': props.center}">
                {{props.label}}
                <slot name="label"/>
            </span>
            <span class="form-switch__box" :class="{'justify-center': props.center}">
                <span v-if="displayedOffText" class="form-switch__text form-switch__off">{{ displayedOffText }}</span>
                <input type="checkbox" ref="inputElement" v-model="submitValue" :value="props.trueValue" :true-value="props.trueValue" :false-value="props.falseValue" :disabled="props.disabled" @change="emit('change')">
                <span class="lever"></span>
                <span v-if="displayedOnText" class="form-switch__text form-switch__on">{{ displayedOnText }}</span>
                <span v-if="$slots.default" class="ml-32 w-full">
                    <slot/>
                </span>
            </span>
        </label>
        <div v-if="!hideDetails" class="form-switch__details">
            <span v-if="hasError && errorMessage" class="form-switch__message" v-html="errorMessage"/>
            <span v-else-if="props.help" class="form-switch__message" v-html="props.help"/>
        </div>
    </div>
</template>

<script setup lang="ts">

import {ToggleInputComponentEmits, type ToggleInputComponentProps, useToggleInputComponent} from "../../../../composables/useToggleInputComponent";
import {ref, computed} from "vue";
import type {Ref} from "vue";
import {useI18n} from "#imports";

const props = withDefaults(defineProps<{
	// The text to label the 'on' value
	onText?: string | boolean | null,

	// The text to label the 'off' value
	offText?: string | boolean | null,

	// Whether to keep the column alignment of the switch for mobile devices
	// Otherwise, the switch will be aligned in a row to meet the classical row design of mobile switches
	forceColumn?: boolean,

	// Whether to keep the mobile row alignment of the switch for desktop devices
	forceRow?: boolean,

	center?: boolean,
} & ToggleInputComponentProps>(), {
	trueValue: true,
	falseValue: false,
	onText: null,
	offText: null,
});

const emit = defineEmits(ToggleInputComponentEmits);

const inputElement: Ref<HTMLElement | null> = ref(null);

const {submitValue, isActive, hasError, errorMessage} = useToggleInputComponent(inputElement, props, emit)

const {t} = useI18n();

const displayedOnText = computed(() => {
	if (props.onText === false) {
		return null;
	}

	return props.onText ?? t('label.yes')
});

const displayedOffText = computed(() => {
	if (props.offText === false) {
		return null;
	}

	return props.offText ?? t('label.no')
});

</script>

<style>
/*********************
  Inputs
**********************/
/*********************
  Typography
**********************/
/*********************
  Inputs
**********************/
/*********************
  Shadows
**********************/
/*********************
  Layout
**********************/
/*********************
  Card
**********************/
/*********************
  Opacity
**********************/
.form-switch,
.form-switch * {
  -webkit-tap-highlight-color: transparent;
  user-select: none;
}

.form-switch {
  min-height: 40px;
}
.form-switch .form-switch__content.form-switch__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.form-switch .form-switch__content.form-switch__row .form-switch__label {
  margin-bottom: 0;
  margin-top: 0;
  margin-right: 8px;
  justify-content: start !important;
}
@media (max-width: 767px) {
  .form-switch .form-switch__content:not(.form-switch__column) {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .form-switch .form-switch__content:not(.form-switch__column) .form-switch__label {
    margin-bottom: 0;
    margin-top: 0;
    margin-right: 8px;
    justify-content: start !important;
  }
}
.form-switch + .form-switch {
  margin-top: 20px;
}
.form-switch .form-switch__label {
  font-size: 13.5px;
  margin-bottom: 8px;
  margin-top: -6px;
  line-height: 1.2;
  display: inline-block;
  color: #465050;
}
.form-switch .form-switch__box {
  cursor: pointer;
  display: flex;
  align-items: center;
}
.form-switch .form-switch__box input[type=checkbox] {
  opacity: 0;
  width: 0;
  height: 0;
}
.form-switch .form-switch__box input[type=checkbox]:checked + .lever {
  background-color: #09ac09;
}
.form-switch .form-switch__box input[type=checkbox]:checked + .lever:before, .form-switch .form-switch__box input[type=checkbox]:checked + .lever:after {
  left: 18px;
}
.form-switch .form-switch__box input[type=checkbox]:checked + .lever:after {
  background-color: #008200;
}
.form-switch .form-switch__box input[type=checkbox]:checked:not(:disabled) ~ .lever:active::before,
.form-switch .form-switch__box input[type=checkbox]:checked:not(:disabled).tabbed:focus ~ .lever::before {
  transform: scale(2.4);
  background-color: rgba(0, 130, 0, 0.15);
}
.form-switch .form-switch__box input[type=checkbox]:not(:disabled) ~ .lever:active:before,
.form-switch .form-switch__box input[type=checkbox]:not(:disabled).tabbed:focus ~ .lever::before {
  transform: scale(2.4);
  background-color: rgba(0, 0, 0, 0.08);
}
.form-switch .form-switch__box input[type=checkbox][disabled] + .lever, .form-switch .form-switch__box input[type=checkbox][disabled]:checked + .lever {
  cursor: default;
  background-color: rgba(0, 0, 0, 0.12);
}
.form-switch .form-switch__box input[type=checkbox][disabled] + .lever:after,
.form-switch .form-switch__box input[type=checkbox][disabled]:checked + .lever:after {
  background-color: #7D8484;
}
.form-switch .form-switch__box .lever {
  content: "";
  display: inline-block;
  position: relative;
  width: 36px;
  min-width: 36px;
  height: 14px;
  background-color: rgba(0, 0, 0, 0.38);
  border-radius: 15px;
  transition: background 0.3s ease;
  vertical-align: middle;
}
.form-switch .form-switch__box .lever:before, .form-switch .form-switch__box .lever:after {
  content: "";
  position: absolute;
  display: inline-block;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  left: 0;
  top: -3px;
  transition: left 0.3s ease, background 0.3s ease, box-shadow 0.1s ease, transform 0.1s ease;
}
.form-switch .form-switch__box .lever:before {
  background-color: rgba(0, 130, 0, 0.15);
}
.form-switch .form-switch__box .lever:after {
  background-color: #f4f4f5;
  box-shadow: 0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12);
}
.form-switch .form-switch__box .form-switch__text {
  font-size: 14px;
  line-height: 1;
}
.form-switch .form-switch__box .form-switch__off {
  margin-right: 8px;
}
.form-switch .form-switch__box .form-switch__on {
  margin-left: 8px;
  color: #7D8484;
}
.form-switch.is-active .form-switch__off {
  color: #7D8484;
}
.form-switch.is-active .form-switch__on {
  color: #008200;
}
.form-switch .form-switch__details {
  display: flex;
  width: 100%;
  margin-top: 6px;
  margin-bottom: 12px;
}
.form-switch .form-switch__details > .form-switch__message {
  text-align: left;
  font-size: 11px;
  color: #5e6b6b;
  line-height: 1.2;
}
</style>